import {
  createEntityAdapter,
  createSlice,
  type PayloadAction,
  type EntityState,
} from '@reduxjs/toolkit';

export interface UploadableFile {
  id: string;
  name: string;
  type?: string;
  progress: number;
  error?: string | null;
  completed?: boolean;
}

export interface UploadFilesState {
  files: EntityState<UploadableFile>;
  uploading: boolean;
  error: string | null;
}

const filesAdapter = createEntityAdapter<UploadableFile>({
  selectId: (file) => file.id,
});

export const initialState: UploadFilesState = {
  files: filesAdapter.getInitialState(),
  uploading: false,
  error: null,
};

const slice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    addFile(
      state,
      action: PayloadAction<Pick<UploadableFile, 'id' | 'name' | 'type'>>
    ) {
      filesAdapter.addOne(state.files, {
        id: action.payload.id,
        name: action.payload.name,
        type: action.payload.type,
        progress: 0,
        error: null,
        completed: false,
      });
    },
    removeFile(state, action: PayloadAction<string>) {
      filesAdapter.removeOne(state.files, action.payload);
    },
    updateProgress(
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) {
      filesAdapter.updateOne(state.files, {
        id: action.payload.id,
        changes: { progress: action.payload.progress },
      });
    },
    setUploading(state, action: PayloadAction<boolean>) {
      state.uploading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setFileError(
      state,
      action: PayloadAction<{ id: string; error: string | null }>
    ) {
      filesAdapter.updateOne(state.files, {
        id: action.payload.id,
        changes: { error: action.payload.error },
      });
    },
    setFileCompleted(
      state,
      action: PayloadAction<{ id: string; completed: boolean }>
    ) {
      filesAdapter.updateOne(state.files, {
        id: action.payload.id,
        changes: { completed: action.payload.completed },
      });
    },
    clearAllFileErrors(state) {
      const updates = state.files.ids.map((id) => ({
        id: id as string,
        changes: { error: null as string | null },
      }));
      filesAdapter.updateMany(state.files, updates);
    },
    reset(state) {
      state.files = filesAdapter.getInitialState();
      state.uploading = false;
      state.error = null;
    },
  },
});

export const {
  reducer: uploadReducer,
  actions: uploadActions,
} = slice;

export const uploadProjectFilesSelectors = filesAdapter.getSelectors<
  UploadFilesState
>((state) => state.files);


